import { darken } from "polished";
import React, {
  useState,
  useRef,
  DragEvent,
  SyntheticEvent,
  ChangeEvent
} from "react";
import styled from "styled-components/macro";
import { cssVarStr } from "../../../utils/react";

export interface FileSelectorProps {
  files?: File[];
  allowedTypes?: string;
  invaidTypeErrorMessage?: string;
  onChange?: (files: File[]) => void;
}

export function FileSelector(props: FileSelectorProps): JSX.Element {
  const refFileInput = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const { files, onChange } = props;

  const preventDefault = (event: SyntheticEvent): void => {
    event.stopPropagation();
    event.preventDefault();
  };

  const onDragEnter = (event: DragEvent): void => {
    setErrors([]);

    preventDefault(event);
    setIsDraggingOver(true);
  };

  const onDragLeave = (event: DragEvent): void => {
    preventDefault(event);
    setIsDraggingOver(false);
  };

  const onDragOver = (event: DragEvent): void => {
    preventDefault(event);
    event.dataTransfer.dropEffect = "copy";
    setIsDraggingOver(true);
  };

  const handleFiles = (fileList: FileList): void => {
    const files = fileList && fileList.length !== 0 ? Array.from(fileList) : [];

    if (!validate(fileList, props.allowedTypes)) {
      return;
    }

    files.forEach((file) => {
      const reader = new FileReader();
      reader.readAsText(file);
    });

    onChange?.(files);
  };

  const validate = (fileList: FileList, allowedTypes: string): boolean => {
    if (fileList.length > 1) {
      setErrors((e) => [...e, "Only one file can be uploaded at one time"]);
      clearFiles();
      return false;
    }

    if (allowedTypes) {
      const files = fileList && fileList.length !== 0 ? Array.from(fileList) : [];
      if (files.some(x => !x.type || allowedTypes.indexOf(x.type) === -1)) {
        setErrors((e) => [...e, props.invaidTypeErrorMessage ?? `This file type is not allowed. Allowed types: ${props.allowedTypes}`]);
        clearFiles();
        return false;
      }
    }

    setErrors([]);
    return true;
  }

  const onDrop = (event: DragEvent): void => {
    preventDefault(event);
    const fileList = event.dataTransfer.files;
    setIsDraggingOver(false);
    handleFiles(fileList);
  };

  const clearFiles = (): void => {
    onChange?.([]);
  }

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
    handleFiles(e.target.files);
  };

  return (
    <Wrapper>
      <DropArea
        id="drop-area"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f2f2f8",
          border: "dashed 1px gray",
          borderColor: isDraggingOver ? "purple" : "gray",
          borderRadius: "4px",
          padding: "20px",
          marginBottom: "20px"
        }}
      >
        <div>Drag and drop a file here</div>
        <div>OR</div>
        <div>
          <FileInput
            ref={refFileInput}
            name="file"
            type="file"
            aria-label="Select a file"
            accept={props.allowedTypes}
            onChange={handleOnChange}
          />
          <label
            htmlFor="file"
            onClick={() => {
              setErrors([]);
              refFileInput.current.click();
            }}
          >
            Select a file
          </label>
        </div>
      </DropArea>
      <div>
        {files &&
          files.length !== 0 &&
          files.map((f) => <div key={f.name}>{f.name}</div>)}
        {(!files || files.length === 0) && <div>No file is selected</div>}
      </div>
      {errors && errors.length !== 0 && (
        <div className="error">
          {errors.map((error, i) => (
            <div key={i}>{error}</div>
          ))}
        </div>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  >* + * {
    margin-top: .5rem;
  }
`;

const DropArea = styled.div`
  >* + * {
    margin-top: 1rem;
  }
`;

const FileInput = styled.input`
  width: 0;
  height: 0;
  opacity: 0;
  z-index: -1;
  overflow: hidden;
  position: absolute;

  & + label {
    font-size: 1.25em;
    font-weight: 600;
    color: white;
    background-color: var(--color-background);
    color: var(--color-on-background);
    border: solid 1px ${darken(0.3, cssVarStr("--color-background"))};    display: inline-block;
    cursor: pointer;
    border-radius: .2rem;
    padding: .5rem 2rem;
  }

  &:focus + label {
    outline: 1px dotted #000;
    outline: -webkit-focus-ring-color auto .5rem;
  }
`;